import React from "react";
import useSWR from "swr";
import { api, STATUS_TO_PILL } from "@/lib/api";
import { CrudList } from "@/components/CrudList";
import { toast } from "sonner";

const fetcher = (u) => api.get(u).then(r=>r.data);

const useUnitOptions = () => {
  const { data: units } = useSWR("/units", fetcher);
  return (units||[]).map(u => ({ value: u.unit_id, label: u.name || u.unit_id }));
};

export const ProductsPage = () => {
  const { data: allProducts } = useSWR("/products", fetcher);
  const { data: allRMs } = useSWR("/raw_materials", fetcher);
  const prodOpts = (allProducts||[]).map(p => ({ value: p.product_id, label: `${p.code ? p.code + " · " : ""}${p.name}` }));
  const rmOpts = (allRMs||[]).map(m => ({ value: m.material_id, label: `${m.code ? m.code + " · " : ""}${m.name}` }));
  return (
  <CrudList title="Products" endpoint="/products" testidPrefix="products" idField="product_id" statusField="status"
    columns={[
      { key:"code", label:"Code" }, { key:"name", label:"Name" }, { key:"category", label:"Category" },
      { key:"material", label:"Material" }, { key:"weight_g", label:"Wt (g)" }, { key:"cavity", label:"Cavity" },
      { key:"cycle_sec", label:"Cycle" },
    ]}
    formFields={[
      { key:"code", label:"Code", required:true }, { key:"name", label:"Name", required:true, full:true },
      { key:"category", label:"Category", type:"select", options:["BOTTLE","CAP","CONTAINER","JAR","JERRY_CAN","OTHER"] },
      { key:"material", label:"Material", type:"select", options:["HDPE","PP","PPCP","LLDPE"] },
      { key:"colour", label:"Colour" }, { key:"weight_g", label:"Weight (g) — legacy", type:"number" },
      { key:"container_weight_g", label:"Container Weight (g)", type:"number" },
      { key:"cap_weight_g", label:"Cap Weight (g)", type:"number" },
      { key:"inner_weight_g", label:"Inner Weight (g)", type:"number" },
      { key:"cap_product_id", label:"Linked Cap Product", type:"select", options: prodOpts },
      { key:"inner_product_id", label:"Linked Inner Product", type:"select", options: prodOpts },
      { key:"masterbatch_pct", label:"Masterbatch %", type:"number" },
      { key:"regrind_pct", label:"Regrind Allowed %", type:"number" },
      { key:"base_material_id", label:"Base Polymer RM (for MRP)", type:"select", options: rmOpts },
      { key:"mb_material_id", label:"Masterbatch RM (for MRP)", type:"select", options: rmOpts },
      { key:"volume_ml", label:"Volume (ml)", type:"number" }, { key:"neck_mm", label:"Neck (mm)", type:"number" },
      { key:"cavity", label:"Cavity", type:"number" }, { key:"cycle_sec", label:"Cycle (s)", type:"number" },
      { key:"machine_type", label:"Machine Type", type:"select", options:["EBM","IBM","IMM"] },
      { key:"carton_qty", label:"Carton Qty", type:"number" },
    ]}
  />
  );
};

export const MachinesPage = () => {
  const unitOptions = useUnitOptions();
  return (
  <CrudList title="Machines" endpoint="/machines" testidPrefix="machines" idField="machine_id" statusField="status"
    columns={[
      { key:"code", label:"Code" }, { key:"name", label:"Name" }, { key:"type", label:"Type" },
      { key:"tonnage", label:"Tonnage" }, { key:"unit_id", label:"Unit" },
      { key:"status", label:"Status" },
    ]}
    formFields={[
      { key:"code", label:"Code", required:true }, { key:"name", label:"Name", required:true },
      { key:"manufacturer", label:"Manufacturer" },
      { key:"type", label:"Type", type:"select", options:["EBM","IBM","IMM","CHILLER","COMPRESSOR","OTHER"] },
      { key:"tonnage", label:"Tonnage", type:"number" },
      { key:"unit_id", label:"Unit", type:"select", options: unitOptions },
      { key:"status", label:"Status", type:"select", options:["RUNNING","IDLE","SETUP","BREAKDOWN","MAINTENANCE"] },
      { key:"installation_date", label:"Installation Date", type:"date" },
    ]}
  />
  );
};

export const MouldsPage = () => {
  const { data: machines } = useSWR("/machines", fetcher);
  const { data: products } = useSWR("/products", fetcher);
  const machOpts = (machines||[])
    .filter(m => !["COMPRESSOR","CHILLER","OTHER"].includes(m.type))
    .map(m => ({ value: m.machine_id, label: `${m.code ? m.code + " · " : ""}${m.name}` }));
  const prodOpts = (products||[]).map(p => ({ value: p.product_id, label: `${p.code ? p.code + " · " : ""}${p.name}` }));
  return (
  <CrudList title="Moulds" endpoint="/moulds" testidPrefix="moulds" idField="mould_id" statusField="status"
    columns={[
      { key:"code", label:"Code" }, { key:"name", label:"Name" }, { key:"cavity", label:"Cavities" },
      { key:"cycle_time_sec", label:"Cycle(s)" },
      { key:"actual_shots", label:"Shots" }, { key:"expected_life", label:"Life" }, { key:"status", label:"Status" },
    ]}
    formFields={[
      { key:"code", label:"Code", required:true }, { key:"name", label:"Name", required:true },
      { key:"cavity", label:"Cavities", type:"number", required:true },
      { key:"cycle_time_sec", label:"Cycle time (seconds)", type:"number" },
      { key:"compatible_machines", label:"Compatible Machines (Ctrl/Cmd click)", type:"multiselect", options: machOpts, full:true },
      { key:"product_ids", label:"Products made (Ctrl/Cmd click)", type:"multiselect", options: prodOpts, full:true },
      { key:"expected_life", label:"Expected life shots", type:"number" },
      { key:"actual_shots", label:"Actual shots", type:"number" },
      { key:"manufacturer", label:"Manufacturer" },
      { key:"status", label:"Status", type:"select", options:["ACTIVE","MAINTENANCE","RETIRED"] },
    ]}
  />
  );
};

export const RawMaterialsPage = () => {
  const { data: allSuppliers } = useSWR("/suppliers", fetcher);
  const supOpts = (allSuppliers||[]).map(s => ({ value: s.supplier_id, label: s.name }));
  return (
  <CrudList title="Raw Materials" endpoint="/raw_materials" testidPrefix="raw-materials" idField="material_id"
    columns={[
      { key:"code", label:"Code" }, { key:"name", label:"Name" }, { key:"material_type", label:"Type" },
      { key:"grade", label:"Grade" }, { key:"uom", label:"UOM" },
      { key:"reorder_level", label:"Reorder Lvl" }, { key:"rate", label:"Rate" },
    ]}
    formFields={[
      { key:"code", label:"Code", required:true }, { key:"name", label:"Name", required:true },
      { key:"material_type", label:"Material Type", type:"select", options:["HDPE","PP","PPCP","LLDPE","Masterbatch","Packing"] },
      { key:"grade", label:"Material Grade" },
      { key:"category", label:"Category (legacy)", type:"select", options:["RESIN","MASTERBATCH","ADDITIVE","PACKAGING","OTHER"] },
      { key:"uom", label:"UOM", type:"select", options:["kg","pcs","ltr"] },
      { key:"reorder_level", label:"Reorder Level (kg)", type:"number" },
      { key:"lead_time_days", label:"Lead Time (days)", type:"number" },
      { key:"bag_size", label:"Bag Size (kg)", type:"number" },
      { key:"rate", label:"Std Rate", type:"number" },
      { key:"preferred_supplier_id", label:"Preferred Supplier", type:"select", options: supOpts },
    ]}
  />
  );
};

export const WarehousesPage = () => {
  const unitOptions = useUnitOptions();
  return (
  <CrudList title="Warehouses" endpoint="/warehouses" testidPrefix="warehouses" idField="warehouse_id"
    columns={[{key:"warehouse_id",label:"ID"},{key:"name",label:"Name"},{key:"type",label:"Type"},{key:"unit_id",label:"Unit"}]}
    formFields={[
      { key:"name", label:"Name", required:true },
      { key:"type", label:"Type", type:"select", options:["RAW_MATERIAL","PACKAGING","WIP","FINISHED_GOODS","REJECTED","SCRAP"] },
      { key:"unit_id", label:"Unit", type:"select", options: unitOptions },
    ]}
  />
  );
};

export const SuppliersPage = () => (
  <CrudList title="Suppliers" endpoint="/suppliers" testidPrefix="suppliers" idField="supplier_id"
    columns={[{key:"name",label:"Name"},{key:"gst",label:"GST"},{key:"phone",label:"Phone"},{key:"email",label:"Email"}]}
    formFields={[
      { key:"name", label:"Name", required:true }, { key:"gst", label:"GST" },
      { key:"contact", label:"Contact Person" }, { key:"phone", label:"Phone" }, { key:"email", label:"Email" },
      { key:"address", label:"Address", type:"textarea", full:true },
    ]}
  />
);

export const CustomersPage = () => (
  <CrudList title="Customers" endpoint="/customers" testidPrefix="customers" idField="customer_id"
    columns={[{key:"name",label:"Name"},{key:"gst",label:"GST"},{key:"credit_days",label:"Credit"},{key:"phone",label:"Phone"}]}
    formFields={[
      { key:"name", label:"Name", required:true }, { key:"gst", label:"GST" },
      { key:"credit_days", label:"Credit Days", type:"number" },
      { key:"phone", label:"Phone" }, { key:"email", label:"Email" },
      { key:"address", label:"Address", type:"textarea", full:true },
    ]}
  />
);

export const UnitsPage = () => (
  <CrudList title="Units" endpoint="/units" testidPrefix="units" idField="unit_id"
    columns={[{key:"unit_id",label:"ID"},{key:"name",label:"Name"},{key:"location",label:"Location"},{key:"gst",label:"GST"}]}
    formFields={[{ key:"name", label:"Name", required:true }, { key:"location", label:"Location", full:true }, { key:"gst", label:"GST" }]}
  />
);

export const BOMPage = () => {
  const { data: boms } = useSWR("/boms", fetcher);
  const { data: products } = useSWR("/products", fetcher);
  const { data: materials } = useSWR("/raw_materials", fetcher);
  const pMap = Object.fromEntries((products||[]).map(p=>[p.product_id,p]));
  const mMap = Object.fromEntries((materials||[]).map(m=>[m.material_id,m]));
  return (
    <div className="space-y-4" data-testid="bom-page">
      <h1 className="text-2xl font-bold text-slate-900">Bill of Materials</h1>
      <div className="space-y-3">
        {(boms||[]).map(b=>(
          <div key={b.bom_id} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-semibold text-slate-900">{pMap[b.product_id]?.name || b.product_id}</div>
                <div className="text-xs font-mono text-slate-500">{b.bom_id} · Rev {b.revision}</div>
              </div>
              {b.active && <span className="pill pill-approved">ACTIVE</span>}
            </div>
            <table className="data-table">
              <thead><tr><th>Material</th><th>Qty / unit</th><th>UOM</th><th>Scrap %</th></tr></thead>
              <tbody>
                {(b.items||[]).map((it,i)=>(
                  <tr key={i}>
                    <td>{mMap[it.material_id]?.name || it.material_id}</td>
                    <td className="font-mono">{it.qty_per_unit}</td>
                    <td>{it.uom}</td>
                    <td>{it.scrap_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
};

export const UsersPage = () => {
  const { data: users, mutate: reload } = useSWR("/users", fetcher);
  const roles = ["CEO_ADMIN","PRODUCTION_MANAGER","PRODUCTION_SUPERVISOR","QC","STORES","PURCHASE","SALES","MAINTENANCE","ACCOUNTS","OPERATOR"];
  const change = async (uid, role) => {
    try { await api.patch(`/users/${uid}`, { role }); toast.success("Role updated"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  return (
    <div className="space-y-4" data-testid="users-page">
      <h1 className="text-2xl font-bold text-slate-900">Users & Roles</h1>
      <div className="bg-white border border-slate-200 rounded-xl overflow-auto">
        <table className="data-table" data-testid="users-table">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Active</th><th>Last Login</th></tr></thead>
          <tbody>
            {(users||[]).map(u=>(
              <tr key={u.user_id}>
                <td>{u.name}</td><td className="font-mono text-xs">{u.email}</td>
                <td>
                  <select data-testid={`user-role-${u.user_id}`} value={u.role} onChange={e=>change(u.user_id, e.target.value)} className="text-xs border border-slate-200 rounded px-2 py-1">
                    {roles.map(r=><option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td>{u.active===false ? "No" : "Yes"}</td>
                <td className="text-xs text-slate-500">{u.last_login?.slice(0,16) || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const AuditPage = () => {
  const { data: logs } = useSWR("/audit-logs?limit=300", fetcher);
  return (
    <div className="space-y-4" data-testid="audit-page">
      <h1 className="text-2xl font-bold text-slate-900">Audit Trail</h1>
      <div className="bg-white border border-slate-200 rounded-xl overflow-auto max-h-[75vh]">
        <table className="data-table" data-testid="audit-table">
          <thead><tr><th>Time</th><th>User</th><th>Role</th><th>Entity</th><th>Action</th><th>Ref</th></tr></thead>
          <tbody>
            {(logs||[]).map(l=>(
              <tr key={l.audit_id}>
                <td className="text-xs font-mono">{l.timestamp?.slice(0,19).replace("T"," ")}</td>
                <td>{l.user_email}</td><td className="text-[11px]">{l.user_role}</td>
                <td>{l.entity}</td>
                <td><span className="pill pill-idle">{l.action}</span></td>
                <td className="text-xs font-mono">{l.entity_id}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
